// Blending Calculator v2 - shared BAC pill, per-peptide strength + dose pills with mcg/mg unit toggle.

const LIMITS = {
  bac:        { min: 0.1,   max: 10,     label: 'mL' },
  strength:   { min: 0.1,   max: 1000,   label: 'mg' },
  dose_mcg:   { min: 1,     max: 200000, label: 'mcg' },
  dose_mg:    { min: 0.001, max: 200,    label: 'mg' },
};

function validateField(key, value) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return { valid: false, error: 'Enter a number' };
  }
  if (value <= 0) return { valid: false, error: 'Must be greater than 0' };
  const limit = LIMITS[key];
  if (!limit) return { valid: true };
  if (value < limit.min || value > limit.max) {
    return { valid: false, error: `Must be between ${limit.min} and ${limit.max} ${limit.label}` };
  }
  return { valid: true };
}

export const STORAGE_KEY = 'is_blend_calc_last';
export const EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;

export function saveLastBlend(snapshot, storage, nowMs) {
  storage.setItem(STORAGE_KEY, JSON.stringify(Object.assign({}, snapshot, { timestampMs: nowMs })));
}

export function loadLastBlend(storage, nowMs) {
  const raw = storage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const d = JSON.parse(raw);
    if (typeof d.timestampMs !== 'number') return null;
    if (nowMs - d.timestampMs > EXPIRY_MS) return null;
    if (!isFinite(d.bacMl) || d.bacMl <= 0) return null;
    if (!Array.isArray(d.rows) || !d.rows.length) return null;
    const complete = d.rows.filter(r =>
      r && isFinite(r.strengthMg) && r.strengthMg > 0
        && isFinite(r.doseValue) && r.doseValue > 0
        && (r.doseUnit === 'mg' || r.doseUnit === 'mcg')
    );
    if (!complete.length) return null;
    return d;
  } catch {
    return null;
  }
}

export function clearLastBlend(storage) {
  storage.removeItem(STORAGE_KEY);
}

const SX = {
  BARREL_X: 80, BARREL_END: 680, BARREL_W: 600, BARREL_TOP: 22, BARREL_BOTTOM: 55,
  BARREL_HEIGHT: 33, FLUID_TOP: 23.5, FLUID_HEIGHT: 30, SEAL_W: 14, PLATE_W: 6, ROD_END: 740,
};

const PEPTIDE_COLOURS = ['#0E7C7B','#C84CA0','#1F8FBF','#7AC74F','#5A4FCF','#444B57'];
export function colourForIndex(i) { return PEPTIDE_COLOURS[i % PEPTIDE_COLOURS.length]; }

export function toDoseMg(value, unit) {
  if (unit === 'mg') return value;
  if (unit === 'mcg') return value * 0.001;
  throw new Error('Unknown dose unit: ' + unit);
}

export function computeRowVolume({ strengthMg, doseValue, doseUnit, bacMl }) {
  if (!isFinite(strengthMg) || strengthMg <= 0) return null;
  if (!isFinite(doseValue) || doseValue <= 0) return null;
  if (!isFinite(bacMl) || bacMl <= 0) return null;
  return (toDoseMg(doseValue, doseUnit) * bacMl) / strengthMg;
}

export function computeBlend({ rows, bacMl, syringeMl }) {
  const valid = [];
  rows.forEach((r, idx) => {
    const v = computeRowVolume({ strengthMg: r.strengthMg, doseValue: r.doseValue, doseUnit: r.doseUnit, bacMl });
    if (v != null) valid.push({ idx, name: r.name || ('Peptide ' + (idx + 1)), volumeMl: v, doseMg: toDoseMg(r.doseValue, r.doseUnit) });
  });
  if (!valid.length) return { ready: false };
  const totalMl = valid.reduce((s, v) => s + v.volumeMl, 0);
  const overflow = totalMl > syringeMl + 1e-9;
  return { ready: true, contributions: valid, totalMl, units: totalMl * 100, overflow, syringeMl };
}

const STRENGTH_PILLS = [
  { v: '2.5', label: '2.5 mg' },
  { v: '5', label: '5 mg' },
  { v: '10', label: '10 mg' },
  { v: '15', label: '15 mg' },
  { v: '20', label: '20 mg' },
  { v: '500', label: '500 mg (NAD)' },
  { v: '1000', label: '1000 mg (NAD)' },
];
const DOSE_MCG_PILLS = [
  { v: '100', label: '100 mcg' },
  { v: '250', label: '250 mcg' },
  { v: '500', label: '500 mcg' },
  { v: '1000', label: '1 mg' },
];
const DOSE_MG_PILLS = [
  { v: '0.25', label: '0.25 mg' },
  { v: '0.5', label: '0.5 mg' },
  { v: '1', label: '1 mg' },
  { v: '2.5', label: '2.5 mg' },
  { v: '5', label: '5 mg' },
  { v: '10', label: '10 mg' },
  { v: '20', label: '20 mg' },
  { v: '50', label: '50 mg (NAD)' },
  { v: '100', label: '100 mg (NAD)' },
];

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}

function boot() {
  const root = document.querySelector('.is-blend');
  if (!root) return;
  initBlend(root);
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
}

function initBlend(root) {
  const $ = (s) => root.querySelector(s);
  const $$ = (s) => root.querySelectorAll(s);

  const rowsEl = $('#is-blend-rows');
  const addBtn = $('#is-blend-add');
  const sharedBacPills = $$('.is-calc__pill[data-shared-field="bac"]');
  const sharedBacOtherBlock = root.querySelector('.is-calc__other-input[data-shared-field="bac"]');
  const sharedBacOtherInput = sharedBacOtherBlock ? sharedBacOtherBlock.querySelector('input') : null;
  const syringePills = $$('[data-syringe]');
  const resultEmpty = $('#is-blend-result-empty');
  const resultOk = $('#is-blend-result-ok');
  const headlineValue = $('#is-blend-headline-value');
  const headlineUnit = $('#is-blend-headline-unit');
  const subline = $('#is-blend-subline');
  const contribsEl = $('#is-blend-contribs');
  const syringeBandsEl = $('#is-blend-syringe-bands');
  const syringeTicks = $('#is-blend-syringe-ticks');
  const syringeNums = $('#is-blend-syringe-nums');
  const syringeRod = $('#is-blend-syringe-rod');
  const syringePlate = $('#is-blend-syringe-plate');
  const syringeSeal = $('#is-blend-syringe-seal');
  const syringeCaption = $('#is-blend-syringe-caption');
  const overflowBox = $('#is-blend-overflow');
  const overflowMsg = $('#is-blend-overflow-msg');

  const state = {
    nextId: 1,
    rows: [],
    bacMl: null,
    syringeMl: 1,
  };

  function buildPillsHtml(field, options, includeOther, otherUnit) {
    const pills = options.map(opt => {
      const unitAttr = opt.unit ? ` data-unit="${opt.unit}"` : '';
      return `<button type="button" class="is-calc__pill" data-field="${field}" data-value="${opt.v}"${unitAttr} role="radio" aria-checked="false">${opt.label}</button>`;
    }).join('');
    const otherBtn = includeOther ? `<button type="button" class="is-calc__pill is-calc__pill--other" data-field="${field}" data-value="other" role="radio" aria-checked="false">Other</button>` : '';
    return pills + otherBtn;
  }

  function buildRow(rowId) {
    const r = { id: rowId, name: '', strengthMg: null, doseValue: null, doseUnit: 'mcg' };
    state.rows.push(r);
    const wrap = document.createElement('div');
    wrap.className = 'is-blend__row';
    wrap.dataset.rowId = String(rowId);
    wrap.dataset.activeUnit = 'mcg';
    const idx = state.rows.length - 1;
    const colour = colourForIndex(idx);
    const ids = {
      name: 'is-blend-r' + rowId + '-name',
      strengthOther: 'is-blend-r' + rowId + '-strength-other',
      doseOther: 'is-blend-r' + rowId + '-dose-other',
    };

    const strengthPills = buildPillsHtml('strength', STRENGTH_PILLS, true);
    const doseMcgPills = DOSE_MCG_PILLS.map(o => Object.assign({ unit: 'mcg' }, o));
    const doseMgPills = DOSE_MG_PILLS.map(o => Object.assign({ unit: 'mg' }, o));
    const dosePills = buildPillsHtml('dose', doseMcgPills.concat(doseMgPills), true);

    wrap.innerHTML = `
      <div class="is-blend__row-header">
        <h3 class="is-blend__row-title">
          <span class="is-blend__row-num" aria-hidden="true">${idx + 1}</span>
          <span class="is-blend__row-swatch" data-swatch style="background:${colour}"></span>
          <span data-row-label>Peptide ${idx + 1}</span>
        </h3>
        <button type="button" class="is-blend__row-remove" data-remove aria-label="Remove peptide ${idx + 1}">Remove</button>
      </div>
      <div class="is-blend__row-body">
        <div class="is-calc__field">
          <label class="is-calc__label" for="${ids.name}">Name (optional)</label>
          <input id="${ids.name}" class="is-calc__input" type="text" data-field="name" placeholder="e.g. BPC-157" maxlength="40">
        </div>
        <div class="is-calc__field">
          <span class="is-calc__label">Vial strength (mg)</span>
          <div class="is-calc__btn-group" role="radiogroup" aria-label="Vial strength for peptide ${idx + 1}">
            ${strengthPills}
          </div>
          <div class="is-calc__other-input" data-field="strength" hidden>
            <label class="is-calc__label" for="${ids.strengthOther}">Vial strength (mg)</label>
            <input id="${ids.strengthOther}" class="is-calc__input" type="number" inputmode="decimal" step="any" min="0.1" max="1000" placeholder="e.g. 7.5">
            <small class="is-calc__error" hidden></small>
          </div>
        </div>
        <div class="is-calc__field">
          <span class="is-calc__label">Dose per injection</span>
          <div class="is-blend__unit-toggle" role="radiogroup" aria-label="Dose unit for peptide ${idx + 1}">
            <button type="button" class="is-blend__unit-btn is-blend__unit-btn--active" data-unit="mcg" aria-pressed="true">mcg</button>
            <button type="button" class="is-blend__unit-btn" data-unit="mg" aria-pressed="false">mg</button>
          </div>
          <div class="is-calc__btn-group" role="radiogroup" aria-label="Dose for peptide ${idx + 1}">
            ${dosePills}
          </div>
          <div class="is-calc__other-input" data-field="dose" hidden>
            <label class="is-calc__label" for="${ids.doseOther}">Dose</label>
            <input id="${ids.doseOther}" class="is-calc__input" type="number" inputmode="decimal" step="any" min="0.001" placeholder="e.g. 175">
            <small class="is-calc__error" hidden></small>
          </div>
        </div>
      </div>
    `;
    rowsEl.appendChild(wrap);

    // Name input
    wrap.querySelector('[data-field="name"]').addEventListener('input', (e) => {
      markUserInteraction();
      r.name = e.target.value.trim();
      const lbl = wrap.querySelector('[data-row-label]');
      if (lbl) lbl.textContent = r.name || ('Peptide ' + (state.rows.indexOf(r) + 1));
      recompute();
    });

    // Strength pill group + Other
    bindRowPillGroup(wrap, 'strength', (v) => { r.strengthMg = v; recompute(); });

    // Dose pill group + Other. The Other input is validated against the row's
    // active unit (mcg/mg) so the limits track the toggle.
    bindRowPillGroup(wrap, 'dose', (v) => {
      r.doseValue = v;
      recompute();
    }, (clickedPill) => {
      if (clickedPill && clickedPill.dataset.value !== 'other') {
        const u = clickedPill.dataset.unit;
        if (u && u !== r.doseUnit) {
          r.doseUnit = u;
          syncUnitToggle(wrap, u);
        }
      }
    }, () => r.doseUnit === 'mg' ? 'dose_mg' : 'dose_mcg');

    // Unit toggle (mcg / mg)
    wrap.querySelectorAll('.is-blend__unit-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        markUserInteraction();
        const unit = btn.dataset.unit;
        if (r.doseUnit === unit) return;
        r.doseUnit = unit;
        syncUnitToggle(wrap, unit);
        // Clear the dose selection when switching units (the previously-active pill is now hidden)
        wrap.querySelectorAll('.is-calc__pill[data-field="dose"]').forEach(p => {
          p.classList.remove('is-calc__pill--active');
          p.setAttribute('aria-checked', 'false');
        });
        const otherBlock = wrap.querySelector('.is-calc__other-input[data-field="dose"]');
        if (otherBlock) {
          otherBlock.hidden = true;
          const inp = otherBlock.querySelector('input');
          if (inp) inp.value = '';
        }
        r.doseValue = null;
        recompute();
      });
    });

    // Remove button
    wrap.querySelector('[data-remove]').addEventListener('click', () => {
      markUserInteraction();
      if (state.rows.length <= 1) return;
      state.rows = state.rows.filter(x => x !== r);
      wrap.remove();
      relabelRows();
      updateRemoveButtons();
      recompute();
    });

    updateRemoveButtons();
    return wrap;
  }

  function syncUnitToggle(wrap, unit) {
    wrap.dataset.activeUnit = unit;
    wrap.querySelectorAll('.is-blend__unit-btn').forEach(b => {
      const active = b.dataset.unit === unit;
      b.classList.toggle('is-blend__unit-btn--active', active);
      b.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  function bindRowPillGroup(wrap, field, onValue, postClick, getLimitKey) {
    const pills = wrap.querySelectorAll(`.is-calc__pill[data-field="${field}"]`);
    const otherBlock = wrap.querySelector(`.is-calc__other-input[data-field="${field}"]`);
    const otherInput = otherBlock ? otherBlock.querySelector('input') : null;
    const errEl = otherBlock ? otherBlock.querySelector('.is-calc__error') : null;

    pills.forEach(pill => {
      pill.addEventListener('click', () => {
        markUserInteraction();
        pills.forEach(p => {
          p.classList.remove('is-calc__pill--active');
          p.setAttribute('aria-checked', 'false');
        });
        pill.classList.add('is-calc__pill--active');
        pill.setAttribute('aria-checked', 'true');
        if (pill.dataset.value === 'other') {
          if (otherBlock) otherBlock.hidden = false;
          if (otherInput) {
            otherInput.focus();
            const v = parseFloat(otherInput.value);
            onValue(isFinite(v) && v > 0 ? v : null);
          } else {
            onValue(null);
          }
        } else {
          if (otherBlock) {
            otherBlock.hidden = true;
            if (otherInput) otherInput.value = '';
            if (errEl) { errEl.textContent = ''; errEl.hidden = true; }
          }
          onValue(parseFloat(pill.dataset.value));
        }
        if (postClick) postClick(pill);
      });
    });
    if (otherInput) {
      otherInput.addEventListener('input', () => {
        markUserInteraction();
        const raw = otherInput.value.trim();
        if (!raw) {
          onValue(null);
          if (errEl) { errEl.textContent = ''; errEl.hidden = true; }
          return;
        }
        const v = parseFloat(raw);
        const key = getLimitKey ? getLimitKey() : field;
        const check = validateField(key, v);
        if (check.valid) {
          if (errEl) { errEl.textContent = ''; errEl.hidden = true; }
          onValue(v);
        } else {
          if (errEl) { errEl.textContent = check.error; errEl.hidden = false; }
          onValue(null);
        }
      });
    }
  }

  function relabelRows() {
    const rowEls = rowsEl.querySelectorAll('.is-blend__row');
    rowEls.forEach((el, i) => {
      const numEl = el.querySelector('.is-blend__row-num');
      const labelEl = el.querySelector('[data-row-label]');
      const swatch = el.querySelector('[data-swatch]');
      const removeBtn = el.querySelector('[data-remove]');
      if (numEl) numEl.textContent = String(i + 1);
      const r = state.rows[i];
      if (labelEl) labelEl.textContent = (r && r.name) ? r.name : ('Peptide ' + (i + 1));
      if (swatch) swatch.style.background = colourForIndex(i);
      if (removeBtn) removeBtn.setAttribute('aria-label', 'Remove peptide ' + (i + 1));
    });
  }

  function updateRemoveButtons() {
    const buttons = rowsEl.querySelectorAll('[data-remove]');
    buttons.forEach(b => { b.disabled = state.rows.length <= 1; b.style.visibility = state.rows.length <= 1 ? 'hidden' : 'visible'; });
    if (addBtn) addBtn.disabled = state.rows.length >= 6;
  }

  function paintSyringeFrame(capacityMl) {
    const totalUnits = capacityMl * 100;
    const tickEvery = totalUnits <= 50 ? 1 : 2;
    const labelEvery = totalUnits <= 30 ? 5 : (totalUnits <= 50 ? 5 : 10);
    const tItems = [];
    const nItems = [];
    for (let u = 0; u <= totalUnits + 1e-6; u += tickEvery) {
      const x = SX.BARREL_X + (u / totalUnits) * SX.BARREL_W;
      const isLabel = Math.abs((u / labelEvery) - Math.round(u / labelEvery)) < 1e-6;
      const tickH = isLabel ? 7 : 3.5;
      tItems.push('<line class="is-blend__sx-tick" x1="' + x.toFixed(2) + '" y1="' + SX.BARREL_TOP + '" x2="' + x.toFixed(2) + '" y2="' + (SX.BARREL_TOP + tickH).toFixed(2) + '" stroke-width="' + (isLabel ? 1.2 : 0.6) + '"/>');
      if (u > 0 && isLabel) {
        nItems.push('<text class="is-blend__sx-num" x="' + x.toFixed(2) + '" y="71" font-size="11">' + Math.round(u) + '</text>');
      }
    }
    syringeTicks.innerHTML = tItems.join('');
    syringeNums.innerHTML = nItems.join('');
  }

  function paintBands(contributions, capacityMl, totalMl) {
    const overflow = totalMl > capacityMl + 1e-9;
    const denom = overflow ? totalMl : capacityMl;
    const bands = [];
    let cursorMl = 0;
    contributions.forEach((c, i) => {
      const startFrac = cursorMl / denom;
      const endFrac = (cursorMl + c.volumeMl) / denom;
      const x = SX.BARREL_X + startFrac * SX.BARREL_W;
      const w = Math.max(0, (endFrac - startFrac) * SX.BARREL_W);
      bands.push('<rect class="is-blend__sx-band" x="' + x.toFixed(2) + '" y="' + SX.FLUID_TOP + '" width="' + w.toFixed(2) + '" height="' + SX.FLUID_HEIGHT + '" fill="' + colourForIndex(c.idx) + '" fill-opacity="0.78"/>');
      if (i < contributions.length - 1) {
        const dx = (SX.BARREL_X + endFrac * SX.BARREL_W).toFixed(2);
        bands.push('<line class="is-blend__sx-divider" x1="' + dx + '" y1="' + (SX.FLUID_TOP + 1) + '" x2="' + dx + '" y2="' + (SX.FLUID_TOP + SX.FLUID_HEIGHT - 1) + '"/>');
      }
      cursorMl += c.volumeMl;
    });
    if (overflow) {
      const capX = SX.BARREL_X + (capacityMl / totalMl) * SX.BARREL_W;
      bands.push('<line x1="' + capX.toFixed(2) + '" y1="' + (SX.FLUID_TOP - 4) + '" x2="' + capX.toFixed(2) + '" y2="' + (SX.FLUID_TOP + SX.FLUID_HEIGHT + 4) + '" stroke="#c0392b" stroke-width="2" stroke-dasharray="3,2"/>');
      bands.push('<text x="' + capX.toFixed(2) + '" y="' + (SX.FLUID_TOP - 6) + '" fill="#c0392b" font-family="ui-monospace, \'SF Mono\', monospace" font-size="9" font-weight="700" text-anchor="middle">cap</text>');
    }
    syringeBandsEl.innerHTML = bands.join('');
    const drawnFrac = Math.max(0, Math.min(1, cursorMl / denom));
    const sealX = SX.BARREL_X + drawnFrac * SX.BARREL_W;
    syringeSeal.setAttribute('x', sealX.toFixed(2));
    syringePlate.setAttribute('x', (sealX + SX.SEAL_W).toFixed(2));
    const rodStart = sealX + SX.SEAL_W + SX.PLATE_W;
    syringeRod.setAttribute('x', rodStart.toFixed(2));
    syringeRod.setAttribute('width', Math.max(0, SX.ROD_END - rodStart).toFixed(2));
  }

  function renderContributions(contributions) {
    contribsEl.innerHTML = contributions.map(c => {
      const ml = c.volumeMl.toFixed(3);
      const units = (c.volumeMl * 100).toFixed(c.volumeMl * 100 < 10 ? 1 : 0);
      return '<li class="is-blend__contrib-item">' +
        '<span class="is-blend__contrib-swatch" style="background:' + colourForIndex(c.idx) + '"></span>' +
        '<span class="is-blend__contrib-name">' + escapeHtml(c.name) + '</span>' +
        '<span class="is-blend__contrib-vol">' + ml + ' mL (' + units + ' units)</span>' +
        '</li>';
    }).join('');
  }

  let userInteracted = false;
  function markUserInteraction() { userInteracted = true; }
  const restoreChip = $('#is-blend-restore-chip');
  const restoreYesBtn = $('#is-blend-restore-yes');
  const restoreNoBtn = $('#is-blend-restore-no');
  function hideRestoreChip() {
    if (restoreChip) { restoreChip.hidden = true; restoreChip.style.display = 'none'; }
  }

  function snapshotBlend() {
    return {
      bacMl: state.bacMl,
      syringeMl: state.syringeMl,
      rows: state.rows.map(r => ({
        name: r.name || '',
        strengthMg: r.strengthMg,
        doseValue: r.doseValue,
        doseUnit: r.doseUnit,
      })),
    };
  }

  let saveBlendTimer = null;
  function maybeSaveBlend() {
    if (saveBlendTimer) clearTimeout(saveBlendTimer);
    saveBlendTimer = setTimeout(() => {
      saveBlendTimer = null;
      if (!isFinite(state.bacMl) || state.bacMl <= 0) return;
      const complete = state.rows.some(r =>
        isFinite(r.strengthMg) && r.strengthMg > 0
        && isFinite(r.doseValue) && r.doseValue > 0
      );
      if (!complete) return;
      saveLastBlend(snapshotBlend(), window.localStorage, Date.now());
    }, 250);
  }


  // --- Shareable URL state (IS #359) -------------------------------------
  // Rows are encoded as name~strengthMg~doseValue~doseUnit, joined by '|', so a
  // whole blend is one shareable link. Restore replays the existing pill/input
  // handlers rather than poking state directly, so DOM and state cannot diverge.
  function encodeRows() {
    return state.rows.map(r => [
      (r.name || '').replace(/[~|]/g, ' ').trim(),
      isFinite(r.strengthMg) && r.strengthMg > 0 ? r.strengthMg : '',
      isFinite(r.doseValue) && r.doseValue > 0 ? r.doseValue : '',
      r.doseUnit || 'mcg',
    ].join('~')).join('|');
  }

  function writeUrl() {
    if (!window.history || !window.history.replaceState) return;
    const p = new URLSearchParams();
    const filled = state.rows.some(r => (r.name && r.name.trim()) ||
      (isFinite(r.strengthMg) && r.strengthMg > 0) || (isFinite(r.doseValue) && r.doseValue > 0));
    if (filled) p.set('p', encodeRows());
    if (isFinite(state.bacMl) && state.bacMl > 0) p.set('bac', state.bacMl);
    if (isFinite(state.syringeMl) && state.syringeMl > 0 && state.syringeMl !== 1) p.set('syr', state.syringeMl);
    const qs = p.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : ''));
  }

  // Decode the URL into the same shape the localStorage restore uses, then hand
  // it to applyRestoredBlend - it already replays every pill/input interaction
  // in the right order, including the mcg/mg unit toggle before the dose pills.
  function readUrlPrefill() {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('p') && !params.has('bac') && !params.has('syr')) return false;
    const rows = (params.get('p') || '').split('|').filter(Boolean).slice(0, 6).map(chunk => {
      const [name, strength, dose, unit] = chunk.split('~');
      return {
        name: name || '',
        strengthMg: parseFloat(strength),
        doseValue: parseFloat(dose),
        doseUnit: unit === 'mg' ? 'mg' : 'mcg',
      };
    });
    if (!rows.length) return false;
    applyRestoredBlend({
      syringeMl: parseFloat(params.get('syr')),
      bacMl: parseFloat(params.get('bac')),
      rows: rows,
    });
    return true;
  }

  function recompute() {
    writeUrl();
    const blend = computeBlend({ rows: state.rows, bacMl: state.bacMl, syringeMl: state.syringeMl });
    if (!blend.ready || !isFinite(state.bacMl) || state.bacMl <= 0) {
      resultEmpty.hidden = false;
      resultOk.hidden = true;
      overflowBox.hidden = true;
      maybeSaveBlend();
      if (userInteracted) hideRestoreChip();
      if (window.announceCalcResult) window.announceCalcResult(null);
      return;
    }
    resultEmpty.hidden = true;
    paintSyringeFrame(state.syringeMl);

    if (blend.overflow) {
      paintBands(blend.contributions, state.syringeMl, blend.totalMl);
      renderContributions(blend.contributions);
      headlineValue.textContent = blend.totalMl.toFixed(3);
      headlineUnit.textContent = 'mL (over capacity)';
      subline.textContent = 'Total draw of ' + blend.totalMl.toFixed(3) + ' mL exceeds the ' + state.syringeMl + ' mL syringe.';
      syringeCaption.textContent = 'Total ' + blend.totalMl.toFixed(3) + ' mL exceeds syringe capacity (' + state.syringeMl + ' mL).';
      overflowMsg.textContent = 'Total draw is ' + blend.totalMl.toFixed(3) + ' mL but the chosen syringe holds ' + state.syringeMl + ' mL. Pick a larger syringe, increase BAC water per vial (so each contribution is smaller), or split the blend across two injections.';
      overflowBox.hidden = false;
      resultOk.hidden = false;
      if (window.announceCalcResult) window.announceCalcResult(blend.totalMl);
      return;
    }

    overflowBox.hidden = true;
    paintBands(blend.contributions, state.syringeMl, blend.totalMl);
    renderContributions(blend.contributions);
    const totalUnits = blend.units;
    headlineValue.textContent = totalUnits.toFixed(totalUnits < 10 ? 1 : 0);
    headlineUnit.textContent = 'units';
    subline.textContent = '= ' + blend.totalMl.toFixed(3) + ' mL total across ' + blend.contributions.length + ' peptide' + (blend.contributions.length === 1 ? '' : 's') + '.';
    const capLabel = Math.round(state.syringeMl * 100) + ' units (' + state.syringeMl + ' mL)';
    syringeCaption.textContent = 'Pull plunger to ' + totalUnits.toFixed(totalUnits < 10 ? 1 : 0) + ' units on a ' + capLabel + ' syringe.';
    resultOk.hidden = false;
    maybeSaveBlend();
    if (userInteracted) hideRestoreChip();
    if (window.announceCalcResult) window.announceCalcResult(blend.totalMl);
  }

  // Initial: 2 rows
  buildRow(state.nextId++);
  buildRow(state.nextId++);


  // Shared BAC pill group + Other
  function activateSharedPill(pill, raw) {
    sharedBacPills.forEach(p => {
      p.classList.remove('is-calc__pill--active');
      p.setAttribute('aria-checked', 'false');
    });
    pill.classList.add('is-calc__pill--active');
    pill.setAttribute('aria-checked', 'true');
    if (pill.dataset.value === 'other') {
      if (sharedBacOtherBlock) sharedBacOtherBlock.hidden = false;
      if (sharedBacOtherInput) {
        if (raw !== undefined) sharedBacOtherInput.value = String(raw);
        const v = parseFloat(sharedBacOtherInput.value);
        state.bacMl = isFinite(v) && v > 0 ? v : null;
      } else {
        state.bacMl = null;
      }
    } else {
      if (sharedBacOtherBlock) sharedBacOtherBlock.hidden = true;
      state.bacMl = parseFloat(pill.dataset.value);
    }
  }
  sharedBacPills.forEach(pill => {
    pill.addEventListener('click', () => {
      markUserInteraction();
      activateSharedPill(pill);
      if (pill.dataset.value === 'other' && sharedBacOtherInput) sharedBacOtherInput.focus();
      recompute();
    });
  });
  const sharedBacErrEl = sharedBacOtherBlock ? sharedBacOtherBlock.querySelector('.is-calc__error') : null;
  if (sharedBacOtherInput) {
    sharedBacOtherInput.addEventListener('input', () => {
      markUserInteraction();
      const raw = sharedBacOtherInput.value.trim();
      if (!raw) {
        state.bacMl = null;
        if (sharedBacErrEl) { sharedBacErrEl.textContent = ''; sharedBacErrEl.hidden = true; }
        recompute();
        return;
      }
      const v = parseFloat(raw);
      const check = validateField('bac', v);
      if (check.valid) {
        state.bacMl = v;
        if (sharedBacErrEl) { sharedBacErrEl.textContent = ''; sharedBacErrEl.hidden = true; }
      } else {
        state.bacMl = null;
        if (sharedBacErrEl) { sharedBacErrEl.textContent = check.error; sharedBacErrEl.hidden = false; }
      }
      recompute();
    });
  }

  // Syringe pills (no Other - 3 fixed)
  syringePills.forEach(p => {
    p.addEventListener('click', () => {
      markUserInteraction();
      const v = parseFloat(p.dataset.syringe);
      if (!isFinite(v) || v <= 0) return;
      state.syringeMl = v;
      syringePills.forEach(other => {
        const active = other === p;
        other.classList.toggle('is-calc__pill--active', active);
        other.setAttribute('aria-checked', active ? 'true' : 'false');
      });
      recompute();
    });
  });

  addBtn.addEventListener('click', () => {
    markUserInteraction();
    if (state.rows.length >= 6) return;
    buildRow(state.nextId++);
    relabelRows();
    recompute();
  });

  // Restore-banner wiring. The banner is shown by the inline pre-paint script
  // in blending-calculator.liquid; here we apply or clear it on user choice.
  function applyRestoredBlend(d) {
    // Syringe
    if (isFinite(d.syringeMl)) {
      const sp = root.querySelector(`[data-syringe="${d.syringeMl}"]`);
      if (sp) sp.click();
    }
    // Shared BAC
    if (isFinite(d.bacMl)) {
      const presetBac = root.querySelector(`.is-calc__pill[data-shared-field="bac"][data-value="${d.bacMl}"]`);
      if (presetBac && !presetBac.classList.contains('is-calc__pill--other')) {
        presetBac.click();
      } else {
        const otherBac = root.querySelector('.is-calc__pill--other[data-shared-field="bac"]');
        if (otherBac && sharedBacOtherInput) {
          otherBac.click();
          sharedBacOtherInput.value = String(d.bacMl);
          sharedBacOtherInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
    }
    // Rows: replace the default 2 with the saved set
    state.rows.slice().forEach(r => {
      const wrap = rowsEl.querySelector(`.is-blend__row[data-row-id="${r.id}"]`);
      if (wrap) wrap.remove();
    });
    state.rows = [];
    d.rows.forEach(saved => {
      const wrap = buildRow(state.nextId++);
      const row = state.rows[state.rows.length - 1];
      if (saved.name) {
        const nameInput = wrap.querySelector('[data-field="name"]');
        if (nameInput) {
          nameInput.value = saved.name;
          nameInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
      // Set unit toggle first so dose pills are visible for the right unit
      if (saved.doseUnit === 'mg' || saved.doseUnit === 'mcg') {
        const unitBtn = wrap.querySelector(`.is-blend__unit-btn[data-unit="${saved.doseUnit}"]`);
        if (unitBtn && row.doseUnit !== saved.doseUnit) unitBtn.click();
      }
      // Strength
      if (isFinite(saved.strengthMg)) {
        const sp = wrap.querySelector(`.is-calc__pill[data-field="strength"][data-value="${saved.strengthMg}"]`);
        if (sp && !sp.classList.contains('is-calc__pill--other')) {
          sp.click();
        } else {
          const op = wrap.querySelector('.is-calc__pill--other[data-field="strength"]');
          if (op) {
            op.click();
            const inp = wrap.querySelector('.is-calc__other-input[data-field="strength"] input');
            if (inp) {
              inp.value = String(saved.strengthMg);
              inp.dispatchEvent(new Event('input', { bubbles: true }));
            }
          }
        }
      }
      // Dose
      if (isFinite(saved.doseValue)) {
        const dp = wrap.querySelector(`.is-calc__pill[data-field="dose"][data-value="${saved.doseValue}"][data-unit="${saved.doseUnit}"]`);
        if (dp && !dp.classList.contains('is-calc__pill--other')) {
          dp.click();
        } else {
          const op = wrap.querySelector('.is-calc__pill--other[data-field="dose"]');
          if (op) {
            op.click();
            const inp = wrap.querySelector('.is-calc__other-input[data-field="dose"] input');
            if (inp) {
              inp.value = String(saved.doseValue);
              inp.dispatchEvent(new Event('input', { bubbles: true }));
            }
          }
        }
      }
    });
    relabelRows();
    updateRemoveButtons();
    recompute();
  }

  if (restoreYesBtn) {
    restoreYesBtn.addEventListener('click', () => {
      const d = loadLastBlend(window.localStorage, Date.now());
      hideRestoreChip();
      if (d) {
        userInteracted = true;
        applyRestoredBlend(d);
      }
    });
  }
  if (restoreNoBtn) {
    restoreNoBtn.addEventListener('click', () => {
      clearLastBlend(window.localStorage);
      hideRestoreChip();
    });
  }

  // Must run AFTER every pill/input listener is attached - the restore replays
  // real clicks, and the shared bac-water group is wired further down this file.
  // A shared link wins over the default rows and over the restore chip.
  if (readUrlPrefill()) { userInteracted = true; hideRestoreChip(); }
}
// build 1777588135
